import { type ComponentProps, type JSX, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
// ComboBox's own option type rather than the app's: the two differ in what
// `payload` is (`unknown` against a NewsDoc `Block`), and taking the picker's
// keeps a caller's `onSelect` inferring the same parameter it does today.
import { ComboBox, type DefaultValueOption } from '@ttab/elephant-ui'

/**
 * A `ComboBox` as the memo dialog wears them: extra small, outlined, modal so
 * that its popover works inside the dialog, and capped at the width the form
 * column gives it.
 *
 * Every picker here also needs the same two translated strings for its empty
 * and searching states, which is the boilerplate this is mostly here to stop
 * repeating. The one picker it does not cover is the existing-planning search,
 * which fetches its options instead of being given them.
 */
export const MemoComboBox = ({
  options,
  selectedOptions,
  placeholder,
  onSelect,
  max,
  sortOrder,
  autoFocus,
  fitToViewport,
  className = 'max-w-48',
  children
}: {
  options: DefaultValueOption[]
  selectedOptions: DefaultValueOption[]
  placeholder: string
  onSelect: (option: DefaultValueOption) => void
  /** Left out by a filter, which takes as many values as the editor picks. */
  max?: number
  sortOrder?: ComponentProps<typeof ComboBox>['sortOrder']
  /** Set by the one picker the dialog opens on - see the template picker. */
  autoFocus?: boolean
  /** Shows the whole list when the viewport has room - see the template picker. */
  fitToViewport?: boolean
  className?: string
  children?: ReactNode
}): JSX.Element => {
  const { t } = useTranslation()

  return (
    <ComboBox
      max={max}
      size='xs'
      modal={true}
      autoFocus={autoFocus}
      fitToViewport={fitToViewport}
      sortOrder={sortOrder}
      variant='outline'
      className={className}
      options={options}
      selectedOptions={selectedOptions}
      placeholder={placeholder}
      onSelect={onSelect}
      translationStrings={{
        nothingFound: t('common:misc.nothingFound'),
        searching: t('common:misc.searching')
      }}
    >
      {children}
    </ComboBox>
  )
}
